import React, { ReactElement } from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { FormattedMessage } from 'react-intl'
import { SelectedRowSelectIcon } from './row'

const bulkRowActionTransitionName = 'ContrastGrid_bulkEdit'
const bulkRowActionTransitionDuration = 300

export const bulkActionBarHeight = 80

const bulkRowActionsCss = css`
  box-shadow: rgb(153, 153, 153) 0px -2px 80px 1px;
  border-top: 1px solid ${theme.color.neutral50};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${bulkActionBarHeight}px;
  z-index: 999;
  background-color: ${theme.color.white};
  padding-left: 26px;
  display: flex;
  align-items: center;

  &.${bulkRowActionTransitionName}-enter {
    transform: translate(0, 100%);

    &.${bulkRowActionTransitionName}-enter-active {
      transform: translate(0, 0);
      transition: transform ${bulkRowActionTransitionDuration}ms;
    }
  }

  &.${bulkRowActionTransitionName}-exit {
    transform: translate(0, 0);

    &.${bulkRowActionTransitionName}-exit-active {
      transform: translate(0, 100%);
      transition: transform ${bulkRowActionTransitionDuration}ms;
    }
  }
`

const selectedActionsCss = css`
  margin-left: auto;
  margin-right: auto;
`

const actionContainerCss = css`
  display: flex;
  align-items: center;
  padding-right: 200px;
  width: 100%;
`
const actionInnerContainerCss = css`
  display: flex;
  flex: none;
  width: 200px;
  align-items: center;
`

const selectionCountCss = css`
  margin-left: 18px;
`

interface BulkActionBarProps {
  readonly selectedCount: number
  readonly allSelected: boolean
  readonly children: ReactElement<any> | undefined
  readonly onClearSelection: () => void
}

export function BulkActionBar(props: BulkActionBarProps) {
  const { selectedCount, allSelected, children, onClearSelection } = props

  return (
    <TransitionGroup>
      {(allSelected || selectedCount > 0) && (
        <CSSTransition
          classNames={bulkRowActionTransitionName}
          timeout={bulkRowActionTransitionDuration}
        >
          <div key="bulkRowActions" className={bulkRowActionsCss}>
            <div className={actionContainerCss}>
              <div className={actionInnerContainerCss}>
                <SelectedRowSelectIcon large onClick={onClearSelection} />
                <div
                  data-testid={selectionCountID}
                  className={selectionCountCss}
                >
                  {allSelected ? (
                    <FormattedMessage
                      id="detail_all"
                      children={(allMsg) => <CountMsg children={allMsg} />}
                    />
                  ) : (
                    <CountMsg children={selectedCount} />
                  )}
                </div>
              </div>
              <div className={selectedActionsCss}>{children}</div>
            </div>
          </div>
        </CSSTransition>
      )}
    </TransitionGroup>
  )
}

function CountMsg({ children }) {
  return (
    <FormattedMessage
      id="variable_selected"
      values={{
        count: (
          <span
            className={css`
              margin-right: 8px;
              font-size: 14px;
              border-radius: 10px;
              line-height: 1.1;
              font-weight: bold;
              padding: 2px 6px 3px;
              background-color: ${theme.color.turquoise70};
              color: ${theme.color.white};
            `}
            children={children}
          />
        )
      }}
      children={(...nodes) => {
        return (
          <span
            data-testid={selectionCountMsgId}
            className={css`
              display: flex;
              align-items: center;
              font-size: 16px;
            `}
          >
            {nodes.map((t, index) => {
              return <React.Fragment key={index}>{t}</React.Fragment>
            })}
          </span>
        )
      }}
    />
  )
}

// testing hooks
export const selectionCountID = 'contrast-grid-selection-count-4'
export const selectionCountMsgId = 'contrast-grid-selection-count-msg'
